import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { scrollToHomeSection } from '@/utils/scrollToHomeSection';

export default function Nav() {
  const [rawScrolled, setRawScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [heroProgress, setHeroProgress] = useState(0);
  const location = useLocation();

  const isHome = location.pathname === '/';
  const scrolled = isHome ? rawScrolled : true;

  useEffect(() => {
    if (mobileOpen) document.body.classList.add('menu-open');
    else document.body.classList.remove('menu-open');
    return () => document.body.classList.remove('menu-open');
  }, [mobileOpen]);

  useEffect(() => {
    const onScroll = () => setRawScrolled(window.scrollY > 40);
    const onHero = (e: Event) => {
      const detail = (e as CustomEvent<number>).detail;
      if (typeof detail === 'number') setHeroProgress(detail);
    };
    window.addEventListener('scroll', onScroll);
    window.addEventListener('hero-progress', onHero as (e: Event) => void);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('hero-progress', onHero as (e: Event) => void);
    };
  }, []);

  const links = [
    { label: 'Solutions', id: 'solutions' },
    { label: 'Savings', id: 'savings' },
    { label: 'Process', id: 'process' },
    { label: 'Projects', id: 'projects' },
    { label: 'Financing', id: 'financing' },
  ];

  const goToSection = (id: string) => scrollToHomeSection(id);

  const goToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Night mode active when hero has progressed past dusk and we haven't scrolled past hero
  const isNight = !scrolled && heroProgress > 0.35;
  const nightMix = Math.min(1, Math.max(0, (heroProgress - 0.25) / 0.35));

  const linkColor = scrolled
    ? 'text-solar-muted hover:text-solar-green'
    : isNight
      ? 'text-white/90 hover:text-white'
      : 'text-solar-ink/80 hover:text-solar-green';

  const logoTextColor = scrolled
    ? 'text-solar-ink'
    : isNight
      ? 'text-white'
      : 'text-solar-ink';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-solar-divider'
          : isNight
            ? 'bg-black/10 backdrop-blur-[2px]'
            : 'bg-transparent'
      }`}
      style={!scrolled && isNight ? { backgroundColor: `rgba(0,0,0,${0.08 + nightMix * 0.08})` } : undefined}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-[64px]">
        {/* Logo */}
        <button
          type="button"
          onClick={goToTop}
          className="group flex items-center gap-2 whitespace-nowrap bg-transparent border-0 p-0 cursor-pointer"
        >
          <span className="relative w-7 h-7 flex items-center justify-center overflow-hidden rounded-full">
            <i className="ri-sun-line text-solar-green text-2xl relative z-10 drop-shadow-[0_0_1px_rgba(255,255,255,0.6)]"></i>
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-70 group-hover:opacity-100 transition-opacity"
              style={{
                background:
                  'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.55) 47%, rgba(255,255,255,0.15) 55%, transparent 70%)',
                mixBlendMode: 'overlay',
                animation: 'logoSheen 6s ease-in-out infinite',
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{
                boxShadow:
                  'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.08)',
              }}
            />
          </span>
          <span className={`display-font font-semibold text-[17px] tracking-tight transition-colors duration-500 ${logoTextColor}`}>
            Sunfield
          </span>
        </button>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.label}
              type="button"
              onClick={() => goToSection(l.id)}
              className={`text-sm font-medium transition-colors duration-500 bg-transparent border-0 p-0 cursor-pointer whitespace-nowrap ${linkColor}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => goToSection('estimate')}
            className="hidden md:inline-flex items-center h-10 px-5 rounded-[10px] bg-solar-green text-solar-ink text-sm font-semibold whitespace-nowrap hover:bg-[#00c850] transition-colors cursor-pointer border-0"
          >
            Get Estimate
          </button>
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl transition-colors ${scrolled ? 'text-solar-ink' : isNight ? 'text-white' : 'text-solar-ink'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu rendered via portal so it sits above all stacking contexts */}
      {mobileOpen && typeof document !== 'undefined' && createPortal(
        (
          <div
            className="md:hidden fixed left-0 right-0 top-16 bottom-0 bg-white border-t border-solar-divider overflow-y-auto"
            style={{ backgroundColor: '#ffffff', zIndex: 60 }}
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.label}
                  type="button"
                  onClick={() => { setMobileOpen(false); goToSection(l.id); }}
                  className="text-[16px] font-medium text-solar-ink hover:text-solar-green text-left bg-transparent border-0 p-0 cursor-pointer min-h-[48px] flex items-center"
                >
                  {l.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setMobileOpen(false); goToSection('estimate'); }}
                className="inline-flex items-center justify-center min-h-[48px] px-5 rounded-[10px] bg-solar-green text-solar-ink text-[15px] font-semibold whitespace-nowrap border-0 cursor-pointer mt-2"
              >
                Get Estimate
              </button>
            </div>
          </div>
        ),
        document.body
      )}
    </nav>
  );
}